import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar.jsx";
import AdminFooter from "./AdminFooter.jsx";
import TopBar from "./TopBar.jsx";
import SudoModeModal from "./SudoModeModal.jsx";
import useSudoMode from "../hooks/useSudoMode.js";
import client from "../../src/api/client";
import AdminScrollToTopButton from "./AdminScrollToTopButton.jsx";
import "../styles/admin-base.css";

const NAVIGATION = [
  { id: "dashboard", path: "dashboard", allowedRoles: ["SUPER_ADMIN"] },
  { id: "members", path: "members", allowedRoles: ["SUPER_ADMIN"] },
  { id: "approvals", path: "registration-review", allowedRoles: ["SUPER_ADMIN", "APPROVER", "APPROVAL_ADMIN"] },
  { id: "security", path: "id-card-management", allowedRoles: ["SUPER_ADMIN", "ID_CARD_MANAGER"] },
  { id: "tickets", path: "tickets", allowedRoles: ["SUPER_ADMIN", "TICKETS_ADMIN"] },
  { id: "events", path: "event-management", allowedRoles: ["SUPER_ADMIN", "EVENT_MANAGER", "EVENTS_MANAGER", "EVENT_ADMIN"] },
  { id: "venues", path: "venue-management", allowedRoles: ["SUPER_ADMIN", "BOOKING_ADMIN"] },
  { id: "bookings", path: "client-bookings", allowedRoles: ["SUPER_ADMIN", "BOOKING_ADMIN"] },
  { id: "reports", path: "reports-analytics", allowedRoles: ["SUPER_ADMIN"] },
  { id: "audit", path: "audit-log", allowedRoles: ["SUPER_ADMIN"] },
  { id: "settings", path: "admin-settings", allowedRoles: ["SUPER_ADMIN"] },
];

const NAV_LABELS = {
  dashboard: "Dashboard",
  members: "Members",
  approvals: "Approvals",
  tickets: "Tickets",
  events: "Event Management",
  venues: "Venue Management",
  bookings: "Client Bookings",
  reports: "Reports",
  audit: "Audit Log",
  security: "ID Card Management",
  settings: "Admin Settings",
};

const navToPath = NAVIGATION.reduce((accumulator, item) => {
  accumulator[item.id] = item.path;
  return accumulator;
}, {});

const pathToNav = NAVIGATION.reduce((accumulator, item) => {
  accumulator[item.path] = item.id;
  return accumulator;
}, { "ticket-detail": "tickets" });

const hasRoleAccess = (item, userRoles) => {
  if (!item.allowedRoles.length) {
    return true;
  }
  return item.allowedRoles.some((role) => userRoles.includes(role));
};

const normalizeAdminRole = (role) => {
  if (!role) {
    return null;
  }
  const raw = typeof role === "object"
    ? (role.code ?? role.name ?? role.role ?? role.value)
    : role;
  if (!raw) {
    return null;
  }
  const normalized = String(raw).trim().toUpperCase();
  if (!normalized) {
    return null;
  }
  const aliases = {
    EVENTS_MANAGER: "EVENTS_MANAGER",
    EVENTS_ADMIN: "EVENTS_MANAGER",
    EVENT_MANAGER: "EVENTS_MANAGER",
    BOOKING_MANAGER: "BOOKING_ADMIN",
  };
  return aliases[normalized] ?? normalized;
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userRoles, setUserRoles] = useState(() => {
    try {
      const storedUser = localStorage.getItem("adminUser");
      if (!storedUser) {
        return [];
      }

      const user = JSON.parse(storedUser);
      const roles = Array.isArray(user?.roles)
        ? user.roles.map((role) => normalizeAdminRole(role)).filter(Boolean)
        : [];

      return Array.from(new Set(roles.filter(Boolean)));
    } catch (error) {
      console.error("Unable to parse admin roles", error);
      return [];
    }
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("admin-sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const { sudoModalOpen, onSudoSuccess, onSudoCancel } = useSudoMode();

  // Axios interceptor: detect 403 { requiresSudo: true } responses
  useEffect(() => {
    const interceptorId = client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 403 &&
          error.response?.data?.requiresSudo
        ) {
          window.dispatchEvent(new CustomEvent("admin-sudo-required"));
        }
        // Also handle 401 (session expired) — redirect to login
        if (error.response?.status === 401) {
          const isAdminRoute = window.location.pathname.startsWith("/admin");
          const isAuthRoute = error.config?.url?.includes("/auth/");
          if (isAdminRoute && !isAuthRoute) {
            localStorage.removeItem("adminToken");
            localStorage.removeItem("adminUser");
            navigate("/admin/login", { replace: true });
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      client.interceptors.response.eject(interceptorId);
    };
  }, [navigate]);

  useEffect(() => {
    const syncRolesFromStorage = () => {
      try {
        const storedUser = localStorage.getItem("adminUser");
        if (!storedUser) {
          setUserRoles([]);
          return;
        }

        const user = JSON.parse(storedUser);
        const roles = Array.isArray(user?.roles)
          ? user.roles.map((role) => normalizeAdminRole(role)).filter(Boolean)
          : [];
        setUserRoles(Array.from(new Set(roles.filter(Boolean))));
      } catch (error) {
        console.error("Unable to parse admin roles", error);
        setUserRoles([]);
      }
    };

    window.addEventListener("storage", syncRolesFromStorage);
    return () => window.removeEventListener("storage", syncRolesFromStorage);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
    
    // Reset scroll position when navigating between pages
    window.scrollTo(0, 0);
    const contentDiv = document.querySelector('.admin-layout__content');
    if (contentDiv) {
      contentDiv.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const isSuperAdmin = userRoles.includes("SUPER_ADMIN");
  const isTicketsAdmin = userRoles.includes("TICKETS_ADMIN");
  const sidebarEnabled = isSuperAdmin;

  const allowedNavItems = useMemo(() => (
    NAVIGATION
      .filter((item) => hasRoleAccess(item, isSuperAdmin ? ["SUPER_ADMIN"] : userRoles))
      .map((item) => ({
        ...item,
        label: NAV_LABELS[item.id] || item.id,
      }))
  ), [userRoles, isSuperAdmin]);

  const orderedNavItems = useMemo(() => {
    if (!isSuperAdmin && isTicketsAdmin) {
      const prioritized = ["tickets"];
      return allowedNavItems.slice().sort((a, b) => {
        const indexA = prioritized.indexOf(a.id);
        const indexB = prioritized.indexOf(b.id);
        if (indexA === -1 && indexB === -1) {
          return 0;
        }
        if (indexA === -1) {
          return 1;
        }
        if (indexB === -1) {
          return -1;
        }
        return indexA - indexB;
      });
    }
    return allowedNavItems;
  }, [allowedNavItems, isSuperAdmin, isTicketsAdmin]);

  const activeNav = useMemo(() => {
    if (!location.pathname.startsWith("/admin")) {
      return "dashboard";
    }

    const [, , slug = "dashboard"] = location.pathname.replace(/\/+$/, "").split("/");
    return pathToNav[slug] || "dashboard";
  }, [location.pathname]);

  const isStandaloneAdminRoute = location.pathname.startsWith("/admin/notifications");

  const fallbackNav = useMemo(() => {
    if (isSuperAdmin) {
      return "dashboard";
    }
    return orderedNavItems[0]?.id || "dashboard";
  }, [orderedNavItems, isSuperAdmin]);

  const handleNavigate = useCallback((target) => {
    const normalizedTarget = target || "dashboard";
    const destinationItem = NAVIGATION.find((item) => item.id === normalizedTarget);

    if (!destinationItem) {
      return;
    }

    const canAccess = isSuperAdmin || hasRoleAccess(destinationItem, userRoles);
    const resolvedTarget = canAccess ? destinationItem.id : fallbackNav;
    const destination = navToPath[resolvedTarget] || "dashboard";
    const sanitized = String(destination).replace(/^\/+|\/+$/g, "");
    navigate(sanitized ? `/admin/${sanitized}` : "/admin/dashboard");
  }, [fallbackNav, isSuperAdmin, navigate, userRoles]);

  useEffect(() => {
    if (isStandaloneAdminRoute) {
      return;
    }
    if (!orderedNavItems.some((item) => item.id === activeNav) && fallbackNav && activeNav !== fallbackNav) {
      const destination = navToPath[fallbackNav] || "dashboard";
      navigate(destination ? `/admin/${destination}` : "/admin/dashboard", { replace: true });
    }
  }, [activeNav, orderedNavItems, fallbackNav, navigate, isStandaloneAdminRoute]);

  const layoutClassName = sidebarEnabled ? "admin-layout" : "admin-layout admin-layout--no-sidebar";

  const toggleSidebar = sidebarEnabled
    ? () => setSidebarOpen((previous) => !previous)
    : undefined;

  const handleToggleCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("admin-sidebar-collapsed", String(next));
      } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // Sidebar width transition can leave charts with stale canvas width.
    const dispatchResize = () => window.dispatchEvent(new Event("resize"));
    const rafId = window.requestAnimationFrame(dispatchResize);
    const timeoutA = window.setTimeout(dispatchResize, 140);
    const timeoutB = window.setTimeout(dispatchResize, 320);
    const timeoutC = window.setTimeout(dispatchResize, 520);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutA);
      window.clearTimeout(timeoutB);
      window.clearTimeout(timeoutC);
    };
  }, [sidebarCollapsed]);

  const showTopNavTabs = !sidebarEnabled && orderedNavItems.length > 1;

  return (
    <div className={`${layoutClassName}${sidebarCollapsed ? " admin-layout--sidebar-collapsed" : ""}`} data-sidebar-open={sidebarOpen ? "true" : "false"}>
      {sidebarEnabled ? (
        <AdminSidebar
          active={activeNav}
          onNavigate={handleNavigate}
          items={orderedNavItems}
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />
      ) : null}
      <div className="admin-layout__main">
        <TopBar onToggleSidebar={toggleSidebar} />
        {showTopNavTabs ? (
          <div className="admin-tabs" role="tablist" aria-label="Module navigation">
            <div className="admin-tabs__list">
              {orderedNavItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`admin-tabs__trigger ${activeNav === item.id ? "is-active" : ""}`.trim()}
                  onClick={() => handleNavigate(item.id)}
                  role="tab"
                  aria-selected={activeNav === item.id}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="admin-layout__content">
          <Outlet context={{ onNavigate: handleNavigate }} />
        </div>
        <AdminFooter navItems={orderedNavItems} onNavigate={handleNavigate} />
      </div>
      {sidebarEnabled && sidebarOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="admin-layout__overlay"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}
      <SudoModeModal
        open={sudoModalOpen}
        onSuccess={onSudoSuccess}
        onCancel={onSudoCancel}
      />
      <AdminScrollToTopButton />
    </div>
  );
}
